import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { User, Mail, Phone, Home, Building, FileBadge, Edit3, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import Drawer from '../../components/common/Drawer/Drawer';
import api from '../../api/axios';
import styles from './MyProfile.module.css';

const MyProfile = () => {
  const navigate = useNavigate();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [familyMembers, setFamilyMembers] = useState([]);
  
  const [profile, setProfile] = useState({
    fullName: '',
    email: '',
    phone: '',
    flatNumber: '',
    wing: '',
    role: 'resident',
    residentType: 'Owner',
    profileImage: 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png'
  });

  const [formData, setFormData] = useState({ ...profile });

  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const [profileRes, familyRes] = await Promise.all([
        api.get('/users/profile'),
        api.get('/family')
      ]);
      setProfile(profileRes.data);
      setFormData(profileRes.data);
      setFamilyMembers(familyRes.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch profile data');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setProfileImageFile(e.target.files[0]);
      setProfileImagePreview(URL.createObjectURL(e.target.files[0]));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const uploadData = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined) {
          uploadData.append(key, formData[key]);
        }
      });
      if (profileImageFile) {
        uploadData.append('profileImage', profileImageFile);
      }

      const { data } = await api.put('/users/profile', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const updatedProfile = { ...data };
      if (updatedProfile.profileImage && updatedProfile.profileImage.startsWith('/uploads')) {
        updatedProfile.profileImage = `${import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '')}${updatedProfile.profileImage}`;
      }

      setProfile(updatedProfile);
      setFormData(updatedProfile);
      
      // Update local storage user info
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      userInfo.fullName = updatedProfile.fullName;
      userInfo.flatNumber = updatedProfile.flatNumber;
      userInfo.wing = updatedProfile.wing;
      userInfo.phone = updatedProfile.phone;
      userInfo.residentType = updatedProfile.residentType;
      userInfo.profileImage = updatedProfile.profileImage;
      localStorage.setItem('userInfo', JSON.stringify(userInfo));
      
      setIsDrawerOpen(false);
      // Trigger a custom event to notify other components of the update
      window.dispatchEvent(new Event('profileUpdated'));
      toast.success('Profile updated successfully!');
    } catch (err) {
      toast.error('Failed to update profile: ' + (err.response?.data?.message || err.message));
    }
  };

  // Helper to get correct image URL
  const getImageUrl = (url) => {
    if (!url) return 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png';
    if (url.startsWith('http')) return url;
    return `${import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '')}${url}`;
  };

  return (
    <div className={styles.container}>
      <PageHeader 
        title="My Profile" 
        breadcrumb={['Account', 'My Profile']} 
        actionButton={
          <button className={styles.editBtn} onClick={() => {
            setFormData(profile);
            setProfileImageFile(null);
            setProfileImagePreview(getImageUrl(profile.profileImage));
            setIsDrawerOpen(true);
          }}>
            <Edit3 size={16} /> Edit Profile
          </button>
        }
      />

      <div className={styles.profileContent}>
        {/* Left Column: Avatar & Basic */}
        <div className={styles.profileCard}>
          <div className={styles.avatarSection}>
            <img src={getImageUrl(profile.profileImage)} alt="Profile" className={styles.avatarLarge} style={{ objectFit: 'cover' }} />
            <h2>{profile.fullName}</h2>
            <span className={styles.badgeRole}>{profile.role}</span>
          </div>

          <div className={styles.infoList}>
            <div className={styles.infoItem}>
              <Mail size={18} className={styles.infoIcon} />
              <div>
                <span>Email Address</span>
                <strong>{profile.email}</strong>
              </div>
            </div>
            <div className={styles.infoItem}>
              <Phone size={18} className={styles.infoIcon} />
              <div>
                <span>Phone Number</span>
                <strong>{profile.phone}</strong>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Unit & Details */}
        <div className={styles.detailsCard}>
          <h3>Unit Information</h3>
          <div className={styles.gridDetails}>
            <div className={styles.detailBox}>
              <Home size={20} className={styles.boxIcon} />
              <span>House No/Flat No</span>
              <strong>{profile.flatNumber || 'Not Set'}</strong>
            </div>
            <div className={styles.detailBox}>
              <Building size={20} className={styles.boxIcon} />
              <span>Tower/Wings/Lane</span>
              <strong>{profile.wing || 'Not Set'}</strong>
            </div>
            <div className={styles.detailBox}>
              <FileBadge size={20} className={styles.boxIcon} />
              <span>Ownership</span>
              <strong>{profile.residentType || 'Not Set'}</strong>
            </div>
          </div>

          <h3 className={styles.mt4}>Family Members ({familyMembers.length})</h3>
          
          {familyMembers.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
              {familyMembers.map(member => (
                <div key={member._id} className={styles.infoItem} style={{ padding: '10px 12px', background: 'rgba(49, 118, 83, 0.03)', border: '1px solid rgba(49, 118, 83, 0.1)', borderRadius: '8px', cursor: 'default' }}>
                  <Users size={18} className={styles.infoIcon} style={{ color: '#317653' }} />
                  <div>
                    <span style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{member.relation}</span>
                    <strong style={{ display: 'block', fontSize: '13px', color: '#1a4231', marginTop: '2px' }}>{member.name}</strong>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '16px' }}>No family members added yet.</p>
          )}

          <div className={styles.infoItem} style={{ cursor: 'pointer', transition: '0.2s ease', background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '8px', justifyContent: 'center', padding: '10px' }} onClick={() => navigate('/resident/family')}>
             <div style={{ textAlign: 'center' }}>
               <strong style={{ fontSize: '12px', color: '#475569' }}>Manage Family Details &rarr;</strong>
             </div>
          </div>

          <h3 className={styles.mt4}>Emergency Contacts</h3>
          <div className={styles.infoItem}>
            <Phone size={18} className={styles.infoIcon} />
            <div>
              <span>Configure in Resources menu</span>
            </div>
          </div>
        </div>
      </div>

      {/* Slide-over Drawer for Editing */}
      <Drawer 
        isOpen={isDrawerOpen} 
        onClose={() => setIsDrawerOpen(false)} 
        title="Edit Profile"
      >
        <form className="drawerForm" onSubmit={handleSubmit}>
          
          <div className={styles.uploadImageSection} onClick={() => document.getElementById('profileImgInput').click()}>
            <img src={profileImagePreview || getImageUrl(profile.profileImage)} alt="Preview" className={styles.avatarPreview} style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #e5e7eb', cursor: 'pointer' }} />
            <span style={{ display: 'block', marginTop: '8px', fontSize: '12px', color: '#6b7280', cursor: 'pointer' }}>Click to change photo</span>
            <input type="file" id="profileImgInput" style={{ display: 'none' }} accept="image/*" onChange={handleImageChange} />
          </div>

          <div className="formGroup">
            <label>Full Name</label>
            <input name="fullName" value={formData.fullName} onChange={handleChange} required />
          </div>
          <div className="formGroup">
            <label>Email Address</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} required />
          </div>
          <div className="formGroup">
            <label>Phone Number</label>
            <input name="phone" value={formData.phone} onChange={handleChange} />
          </div>
          <div className="formGroup">
            <label>Tower/Wings/Lane</label>
            <input name="wing" value={formData.wing} onChange={handleChange} />
          </div>
          <div className="formGroup">
            <label>House No/Flat No</label>
            <input name="flatNumber" value={formData.flatNumber} onChange={handleChange} />
          </div>
          <div className="formGroup">
            <label>Resident Type</label>
            <select name="residentType" value={formData.residentType} onChange={handleChange}>
              <option value="Owner">Owner</option>
              <option value="Tenant">Tenant</option>
            </select>
          </div>
          
          <div className="formActions">
            <button type="button" className="btnCancel" onClick={() => setIsDrawerOpen(false)}>Cancel</button>
            <button type="submit" className="btnSubmit">Save Changes</button>
          </div>
        </form>
      </Drawer>
    </div>
  );
};

export default MyProfile;
